import { useEffect } from 'react'
import type { EpisodeResponse } from '@/api/types'
import { SKIP_BACK_SEC, SKIP_FORWARD_SEC } from './constants'

// Media Session (медиа-хаб Chrome, экран блокировки, кнопки гарнитуры). В Video.js v10
// своей интеграции нет — тонкая обёртка над navigator.mediaSession.

export interface MediaSessionHandlers {
  play: () => void
  pause: () => void
  seekBy: (deltaSec: number) => void
  seekTo: (timeSec: number) => void
  next: () => void
}

const ACTIONS: MediaSessionAction[] = [
  'play',
  'pause',
  'seekbackward',
  'seekforward',
  'seekto',
  'nexttrack',
]

function hasMediaSession(): boolean {
  return typeof navigator !== 'undefined' && 'mediaSession' in navigator
}

function setHandler(action: MediaSessionAction, handler: MediaSessionActionHandler | null) {
  try {
    navigator.mediaSession.setActionHandler(action, handler)
  } catch {
    // браузер не знает этого action — пропускаем
  }
}

export function useMediaSession(
  episode: EpisodeResponse | null,
  handlers: MediaSessionHandlers,
): void {
  const active = episode !== null

  useEffect(() => {
    if (!active || !hasMediaSession()) return
    setHandler('play', () => handlers.play())
    setHandler('pause', () => handlers.pause())
    setHandler('seekbackward', (d) => handlers.seekBy(-(d.seekOffset ?? SKIP_BACK_SEC)))
    setHandler('seekforward', (d) => handlers.seekBy(d.seekOffset ?? SKIP_FORWARD_SEC))
    setHandler('seekto', (d) => {
      if (d.seekTime != null) handlers.seekTo(d.seekTime)
    })
    setHandler('nexttrack', () => handlers.next())
    return () => {
      for (const action of ACTIONS) setHandler(action, null)
    }
  }, [active, handlers])

  useEffect(() => {
    if (!hasMediaSession()) return
    const session = navigator.mediaSession
    if (!episode) {
      session.metadata = null
      session.playbackState = 'none'
      return
    }
    session.metadata = new MediaMetadata({
      title: episode.title,
      artist: episode.uploader ?? '',
      album: 'podpipe',
      artwork: episode.thumbnailUrl ? [{ src: episode.thumbnailUrl }] : [],
    })
  }, [episode])
}

export function setMediaSessionPlaybackState(state: MediaSessionPlaybackState): void {
  if (hasMediaSession()) navigator.mediaSession.playbackState = state
}

// Позиция для скраббера в медиа-хабе. Между вызовами браузер экстраполирует её сам по
// playbackRate, поэтому звать на разрывах (seek/pause/rate) + редко по таймеру — хватает.
export function syncPositionState(media: HTMLMediaElement): void {
  if (!hasMediaSession() || typeof navigator.mediaSession.setPositionState !== 'function') return
  const { duration, currentTime, playbackRate } = media
  if (!Number.isFinite(duration) || duration <= 0) return
  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: playbackRate || 1,
      position: Math.min(Math.max(currentTime, 0), duration),
    })
  } catch {
    // невалидное сочетание значений на переходных состояниях — пропускаем тик
  }
}
